import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header';
import databg from '@/assets/products/crypto.png';

import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/crypto');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/crypto-cycle-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/crypto-liquidity-map-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/sentiment-divergence-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/catalyst-radar-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const CryptoPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Crypto Data API: Prices, Coins & Digital Assets"
        description="Crypto data API with daily and historical cryptocurrency prices for 10,000+ digital assets across 100+ exchanges, plus on-chain and derivatives data."
        canonicalUrl="https://axionquant.com/products/crypto"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Crypto Data API",
          "description": "Crypto data API with daily and historical cryptocurrency prices for 10,000+ digital assets across 100+ exchanges, plus on-chain and derivatives data.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Crypto Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/01-market-data/crypto"
  title="Crypto Data API & Digital Assets"
  body="Daily and hourly crypto prices across the full universe of coins and tokens, with deep history back to genesis and a complete technical analysis toolkit built for quantitative crypto market data research and systematic trading."
  crumb={{name: 'Crypto', path:'/products/crypto'}}
  tabs={[
    {
      label: 'Price & Candlesticks',
      language: 'python',
      filename: 'crypto_candles.py',
      bg: '7.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

df = utils.df(
    client.crypto.prices(
        "BTC",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

visualize.candles(df)
`,
    },
    {
      label: 'Momentum Signals',
      language: 'python',
      filename: 'crypto_momentum.py',
      bg: '8.png',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.crypto.prices("ETH", from_date=utils.d(utils.yearago))
)

df["rsi"] = ta.rsi(df, period=14)

macd_line, signal_line, histogram = ta.macd(df)
df["macd"]      = macd_line
df["signal"]    = signal_line
df["histogram"] = histogram

visualize.graph(
    df,
    x="time",
    lines=["macd", "signal"],
    bars=["histogram"],
    title="ETH - MACD & RSI",
)
`,
    },
    {
      label: 'Volatility Regime',
      language: 'python',
      filename: 'crypto_volatility.py',
      bg: '5.jpg',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.crypto.prices("BTC", from_date=utils.d(utils.yearago))
)

upper, mid, lower = ta.bbands(df, period=20, num_std_dev=2)
df["bb_upper"] = upper
df["bb_mid"]   = mid
df["bb_lower"] = lower
df["atr"]      = ta.atr(df, period=14)

visualize.graph(
    df,
    x="time",
    lines=["close", "bb_upper", "bb_mid", "bb_lower"],
    bars=["atr"],
    title="BTC - Bollinger Bands & ATR",
)
`,
    },
    {
      label: 'Cross-Asset Comparison',
      language: 'python',
      filename: 'crypto_cross_asset.py',
      bg: '6.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

coins   = ["BTC", "ETH", "SOL"]
from_dt = utils.d(utils.yearago)

prices = {
    c: utils.df(client.crypto.prices(c, from_date=from_dt))
    for c in coins
}

df = utils.snap(
    [prices[c] for c in coins],
    names=coins,
    overwrite=["close"],
    col="time",
)

df = utils.relativity(df, cols=coins)

visualize.polls(df)
`,
    },
  ]}
/>
      <Marquee />

      <FeaturesGrid
        title="Crypto Data API & Intelligence. "
        subtitle="Cryptocurrency prices, on-chain analytics, and market data. "
        features={[
          {
            fig: 'FIG 0.1',
            icon: <Zap size={80} color="var(--purple)" />,
            title: 'Daily & Hourly Crypto Data',
            description:
              'Daily and hourly price data from 100+ exchanges with deep history back to genesis. Pull complete market cycles nearly instantly for backtesting and long-horizon research.',
          },
          {
            fig: 'FIG 0.2',
            icon: <Activity size={80} color="var(--green)" />,
            title: 'On-Chain Analytics',
            description:
              'Transaction volumes, active addresses, wallet inflows/outflows, miner flows, and network health metrics across 50+ blockchains, updated daily.',
          },
          {
            fig: 'FIG 0.3',
            icon: <Database size={80} color="var(--red)" />,
            title: 'DeFi Metrics',
            description:
              'Protocol TVL, yield farming APYs, liquidity pool depths, and DEX trading volumes. Track the pulse of decentralized finance from one API.',
          },
          {
            fig: 'FIG 0.4',
            icon: <TrendingDown size={80} color="var(--purple)" />,
            title: 'Derivatives Data',
            description:
              'Futures, options, and perpetual swap data-funding rates, open interest, and liquidations across major derivatives exchanges. Essential for positioning and risk.',
          },
          {
            fig: 'FIG 0.5',
            icon: <Clock size={80} color="var(--green)" />,
            title: 'Historical Data',
            description:
              'Daily OHLCV cryptocurrency data from genesis block to present. Backtest strategies against complete market cycles with clean, adjusted data.',
          },
          {
            fig: 'FIG 0.6',
            icon: <Globe size={80} color="var(--red)" />,
            title: 'Global Exchange & Liquidity',
            description:
              'Consolidated price and volume data from CEXs and DEXs worldwide. Identify arbitrage, liquidity gaps, and best execution venues for digital asset data across 100+ platforms.',
          },
        ]}
      />

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Crypto Data Platform"
        headingPlain="Crypto market data"
        headingEm="from leading exchanges."
        body="Daily and hourly crypto market data from the world's top cryptocurrency exchanges, normalized and retrievable nearly instantly for long-horizon research."
        stats={[
          { v: '100+', l: 'Exchanges' },
          { v: '1M+', l: 'Exchange Pairs' },
          { v: 'Full history', l: 'Data Depth' },
          { v: '99.99%', l: 'Uptime SLA' },
        ]}
        markers={[
          { id: "binance", location: [35.90, 14.50], label: "Binance" },
          { id: "coinbase", location: [37.77, -122.42], label: "Coinbase" },
          { id: "gemini", location: [40.71, -74.01], label: "Gemini" },
          { id: "kraken", location: [37.77, -122.42], label: "Kraken" },
          { id: "okx", location: [-4.62, 55.45], label: "OKX" },
          { id: "bybit", location: [25.20, 55.27], label: "Bybit" },
          { id: "bitfinex", location: [22.28, 114.16], label: "Bitfinex" },
          { id: "bitstamp", location: [49.61, 6.13], label: "Bitstamp" },
          { id: "cryptocom", location: [1.29, 103.85], label: "Crypto.com" },
          { id: "kucoin", location: [1.29, 103.85], label: "KuCoin" },
          { id: "gateio", location: [30.27, 120.15], label: "Gate.io" },
          { id: "mexc", location: [1.29, 103.85], label: "MEXC" },
          { id: "htx", location: [1.29, 103.85], label: "HTX" },
          { id: "bitget", location: [1.29, 103.85], label: "Bitget" },
          { id: "bingx", location: [1.29, 103.85], label: "BingX" },
          { id: "upbit", location: [37.57, 126.98], label: "Upbit" },
          { id: "bithumb", location: [37.57, 126.98], label: "Bithumb" },
          { id: "coincheck", location: [35.68, 139.69], label: "Coincheck" },
          { id: "bitflyer", location: [35.68, 139.69], label: "bitFlyer" },
          { id: "korbit", location: [37.57, 126.98], label: "Korbit" },
          { id: "lbank", location: [22.28, 114.16], label: "LBank" },
          { id: "whitebit", location: [50.45, 30.52], label: "WhiteBIT" },
          { id: "coinsquare", location: [43.65, -79.38], label: "Coinsquare" },
          { id: "ndax", location: [51.05, -114.07], label: "NDAX" },
          { id: "newton", location: [43.65, -79.38], label: "Newton" },
          { id: "shakepay", location: [45.50, -73.57], label: "Shakepay" },

          { id: "ethereum", location: [41.39, 2.17], label: "Ethereum Foundation" },
          { id: "solana", location: [47.38, 8.54], label: "Solana Foundation" },
          { id: "cardano", location: [47.38, 8.54], label: "Cardano Foundation" },
          { id: "polkadot", location: [47.38, 8.54], label: "Web3 Foundation" },
          { id: "avax", location: [40.76, -111.89], label: "Ava Labs" },
          { id: "polygon", location: [19.08, 72.88], label: "Polygon" },
          { id: "chainlink", location: [37.77, -122.42], label: "Chainlink Labs" },
          { id: "ripple", location: [37.79, -122.40], label: "Ripple" },
          { id: "stellar", location: [37.79, -122.40], label: "Stellar" },
          { id: "algorand", location: [42.36, -71.06], label: "Algorand" },
          { id: "near", location: [41.01, 28.97], label: "NEAR Foundation" },
          { id: "tezos", location: [41.39, 2.17], label: "Tezos Foundation" },
          { id: "hedera", location: [32.78, -96.80], label: "Hedera" },
          { id: "aptos", location: [37.77, -122.42], label: "Aptos Labs" },
          { id: "sui", location: [37.77, -122.42], label: "Mysten Labs" },

          { id: "tether", location: [22.28, 114.16], label: "Tether" },
          { id: "circle", location: [42.36, -71.06], label: "Circle" },
          { id: "blockstream", location: [42.36, -71.06], label: "Blockstream" },
          { id: "consensys", location: [40.71, -74.01], label: "Consensys" },

          { id: "marathon", location: [36.17, -115.14], label: "Marathon Digital" },
          { id: "riot", location: [30.27, -97.74], label: "Riot Platforms" },
          { id: "hut8", location: [43.65, -79.38], label: "Hut 8" },
          { id: "hive", location: [49.28, -123.12], label: "HIVE Digital" },
          { id: "cleanspark", location: [33.75, -84.39], label: "CleanSpark" },
          { id: "corescientific", location: [30.27, -97.74], label: "Core Scientific" },
          { id: "irisenergy", location: [-33.87, 151.21], label: "Iris Energy" },
          { id: "bitdeer", location: [1.29, 103.85], label: "Bitdeer" },
          { id: "canaan", location: [30.27, 120.15], label: "Canaan" },
          { id: "bitmain", location: [39.90, 116.40], label: "Bitmain" },
        ]}
      />

      <DataGrid
        title="Crypto Data API: On-Chain & Market Data, Unified"
        subtitle="Bridge the gap between blockchain activity and market moves. Track daily and hourly cryptocurrency data, on-chain flows, exchange dynamics, derivatives positioning, and sentiment across 10,000+ digital assets with institutional-grade reliability."
        bg={databg}
        stats={[
          { label: 'Tokens Tracked', value: '10,000+' },
          { label: 'On-Chain Metrics', value: '200+' },
          { label: 'Exchange Pairs', value: '1M+' },
        ]}
      />
      <CardList
        title="Decipher the Crypto On-Chain Signal"
        vid={'https://cdn.studio14a.com/axion/products/cards/crypto.mp4'}
        points={[
          {
            "title": "On-Chain Analytics",
            "details": "Monitor wallet movements, exchange inflows/outflows, miner activity, and network health metrics like hash rate and transaction volume alongside crypto market data across major blockchains.",
            "cta": "Explore on-chain metrics",
            "url": "/docs"
          },
          {
            "title": "Global Exchange & Liquidity Data",
            "details": "Consolidated price and volume data, funding rates, and spot/derivatives volumes across centralized and decentralized exchanges. Identify arbitrage and liquidity opportunities.",
            "cta": "View exchange coverage",
            "url": "/docs"
          },
          {
            "title": "Tokenomics & Governance Intelligence",
            "details": "Track token supply schedules, vesting unlocks, staking yields, and governance proposal sentiment. Model the fundamental supply-side pressure and community dynamics.",
            "cta": "Analyze token fundamentals",
            "url": "/products/corporate-profiles"
          }
        ]}
      />

      <Responses
        heading='Digital asset market data'
        subheading='Reference metadata and pricing coverage for spot cryptocurrencies and digital assets across global markets'
        ticker="BTC"
        tabs={[
          {
            title: 'Ticker Details',
            description: 'Metadata for a specific cryptocurrency including its unique identifier, full name, and classification type',
            data: {
              "id": 1,
              "ticker": "BTC",
              "name": "Bitcoin",
              "type": "spot",
              "category": "cryptocurrencies",
              "rating": "strongsell",
              "cap": "1248599187015.109900",
              "supply": "20046943.000000",
              "lastClose": 62283.98828125,
              "changePct": -2.60838509580443
            },
          },
          {
            title: 'Historical Prices',
            description: 'Historical OHLCV price data with support for daily, weekly, monthly, quarterly, and yearly aggregations',
            data: [
              {
                time: "2025-12-19T12:43:00.000Z",
                ticker: "BTC",
                open: "85463.3000",
                high: "88292.5500",
                low: "85134.0600",
                close: "87981.6800",
                volume: "59129171968",
              },
              {
                time: "2025-12-18T00:00:00.000Z",
                ticker: "BTC",
                open: "86144.3700",
                high: "89412.6600",
                low: "84436.3100",
                close: "85462.5100",
                volume: "52667115348",
              },
            ],
          },
          {
            title: 'All Tickers',
            description: 'A list of all available cryptocurrency tickers with name and classification',
            data: [
              {
                "id": 1,
                "ticker": "BTC",
                "name": "Bitcoin",
                "type": "spot",
                "category": "cryptocurrencies"
              },
              {
                "id": 2,
                "ticker": "ETH",
                "name": "Ethereum",
                "type": "spot",
                "category": "smart contract platforms"
              },
              {
                "id": 3,
                "ticker": "USDT",
                "name": "Tether USDt",
                "type": "spot",
                "category": "stablecoins"
              },
            ],
          },
          {
            title: 'Latest Quote',
            description: 'Latest quote for a cryptocurrency including latest price, EMAs, 52-week range, and YTD performance',
            data: {
              latestClose: 62283.99,
              latestOpen: 63951.51,
              latestHigh: 64163.45,
              latestLow: 61990.45,
              previousClose: 63952.11,
              week52Range: { high: 126198.07, low: 59108.92 },
              week52AvgClose: 91640.05,
              ema30: 66533.35,
              ema60: 69524.52,
              ema90: 71313.70,
              ema120: 73026.25,
              ytdClosePctChange: -30.75,
              volume: 30031278080,
              avgVolumeLifetime: 22325308811.02,
            },
          },
          {
            title: 'Market Gainers',
            description: 'Top cryptocurrency gainers ranked by percentage change over the last 5 days',
            data: [
              {
                "ticker": "VANA",
                "pctchange": 24077102.262329
              },
              {
                "ticker": "LUNA",
                "pctchange": 29965.2941892873
              },
              {
                "ticker": "SPACE",
                "pctchange": 27397.3324788449
              },
              {
                "ticker": "HEI",
                "pctchange": 17804.1241179401
              },
              {
                "ticker": "SXP",
                "pctchange": 347.540839015397
              }]
          },
          {
            title: 'Market Losers',
            description: 'Top cryptocurrency losers ranked by percentage change over the last 5 days',
            data: [
              {
                "ticker": "ZYPTO",
                "pctchange": -100
              },
              {
                "ticker": "3ULL",
                "pctchange": -82.1428578389002
              },
              {
                "ticker": "ARCA",
                "pctchange": -82.0354057891293
              },
              {
                "ticker": "KFT",
                "pctchange": -66.5785354864335
              },
              {
                "ticker": "NYAN",
                "pctchange": -63.7062847041318
              },
            ],
          },
        ]}
      />
      <DatasetDetails full={true}
        title="Comprehensive Crypto Market Coverage"
        subtitle="Access daily and hourly prices from 100+ exchanges, on-chain transaction data, DeFi protocol metrics, and blockchain analytics for 10,000+ cryptocurrencies and tokens. Power algorithmic trading strategies, risk management systems, and portfolio analytics with institutional-grade crypto data."
        cta="Access Data Now"
        ctaLink="/login"
        tiles={[
          {
            img: '3.webp',
            title: 'We track over 10,000 tokens',
            caption: 'You can monitor cryptocurrencies and tokens across every major blockchain and exchange.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '5.webp',
            title: 'We connect to 100+ exchanges',
            caption: 'You get daily and hourly price data from every major crypto trading venue.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '12.webp',
            title: 'We monitor 50+ blockchains',
            caption: 'You can analyze on-chain transaction data and DeFi protocol metrics across the crypto ecosystem.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '14.webp',
            title: 'We provide over 1B data points',
            caption: 'You can power quantitative crypto research with a massive dataset of market and blockchain data.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '0.webp',
            title: 'We retrieve data in milliseconds',
            caption: 'You can pull complete market cycles nearly instantly to power long-horizon research and backtesting.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Crypto data API numbers that speak for themselves."
        leftCell={{
          stat: '99.99%',
          title: '99.99% Data Accuracy.',
          desc: 'Rigorous validation across all feeds.',
          showChart: true,
        }}
        rightCells={[
          { stat: '1B+', title: '1B+ Data Points.', desc: 'Daily and hourly market and on-chain data.' },
          { stat: '10,000+', title: '10,000+ Tokens.', desc: 'Comprehensive asset coverage.' },
        ]}
      /><ProductFAQ slug="crypto" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default CryptoPage;
